import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
    Inbox, User, Phone, Mail, MapPin, Stethoscope, Building2,
    Calendar, Clock, CheckCircle2, XCircle, Trash2, Search,
    FileText, Plus, RefreshCw, Send, ShieldAlert, Award, Briefcase
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const OnboardingRequests = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [newNote, setNewNote] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const handleInvitePartner = (req) => {
        const isDoc = req.type === 'doctor';
        const role = req.type; // 'doctor' or 'hospital'
        
        let doctorType = '';
        if (isDoc) {
            const spec = (req.specialization || '').toLowerCase();
            if (spec.includes('anesthesiologist') || spec.includes('anaesthesiologist')) {
                doctorType = 'anesthesiologist';
            } else {
                doctorType = 'Surgeon';
            }
        }
        
        const nameToSplit = isDoc ? req.fullName : (req.contactPerson || req.hospitalName || '');
        const nameParts = (nameToSplit || '').trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        const params = new URLSearchParams({
            role,
            doctorType,
            email: req.email || '',
            firstName,
            lastName,
            phone: req.phone || ''
        });
        
        navigate(`/onboarding/generate?${params.toString()}`);
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await api.get('/onboarding-requests');
            setRequests(res.data.data);
            
            // Sync selected request with fresh data if open
            if (selectedRequest) {
                const fresh = res.data.data.find(r => r._id === selectedRequest._id);
                if (fresh) setSelectedRequest(fresh);
            }
        } catch (err) {
            if (!silent) toast.error('Failed to load onboarding requests.');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
        setActionLoading(true);
        try {
            const res = await api.patch(`/onboarding-requests/${id}/status`, { status: newStatus });
            toast.success(`Request marked as ${newStatus}!`);
            fetchRequests(true);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update status.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleAddNote = async (e) => {
        e.preventDefault();
        if (!newNote.trim()) return;
        
        setActionLoading(true);
        try {
            const res = await api.post(`/onboarding-requests/${selectedRequest._id}/notes`, { content: newNote });
            toast.success('Internal note added successfully.');
            setNewNote('');
            fetchRequests(true);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add note.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const confirmed = window.confirm('DANGER: Are you sure you want to permanently delete this onboarding request?');
        if (!confirmed) return;

        setActionLoading(true);
        try {
            await api.delete(`/onboarding-requests/${id}`);
            toast.success('Request deleted successfully.');
            setSelectedRequest(null);
            fetchRequests();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete request.');
        } finally {
            setActionLoading(false);
        }
    };

    // Filter requests
    const filteredRequests = requests.filter(req => {
        const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
        const matchesType = typeFilter === 'all' || req.type === typeFilter;
        
        const nameToSearch = req.type === 'doctor' ? req.fullName : (req.hospitalName || req.contactPerson || '');
        const matchesSearch = 
            nameToSearch.toLowerCase().includes(searchQuery.toLowerCase()) ||
            req.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            req.phone.includes(searchQuery);

        return matchesStatus && matchesType && matchesSearch;
    });

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <span className="bg-amber-100 text-amber-700 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">Pending</span>;
            case 'contacted':
                return <span className="bg-blue-100 text-blue-700 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">Contacted</span>;
            case 'approved':
                return <span className="bg-emerald-100 text-emerald-700 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">Approved</span>;
            case 'rejected':
                return <span className="bg-rose-100 text-rose-700 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">Rejected</span>;
            default:
                return null;
        }
    };

    return (
        <div className="flex h-[calc(100vh-80px)] overflow-hidden bg-slate-50/50">
            {/* Left Column: Requests List */}
            <div className="flex flex-col w-full md:w-1/2 lg:w-5/12 border-r border-slate-100 bg-white h-full">
                {/* Header */}
                <div className="p-6 border-b border-slate-100 space-y-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Inbox className="w-6 h-6 text-[#143E78]" />
                            <h1 className="text-xl font-black text-slate-800">Website Requests</h1>
                        </div>
                        <button 
                            onClick={() => fetchRequests()} 
                            disabled={loading}
                            className="p-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-xl transition-all"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>

                    {/* Search & Filters */}
                    <div className="space-y-3">
                        <div className="relative">
                            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, phone..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 text-xs border border-slate-200 rounded-xl outline-none focus:border-[#143E78] transition-all bg-slate-50/50"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none bg-slate-50/50 font-bold text-slate-600"
                            >
                                <option value="all">All Types</option>
                                <option value="doctor">Doctors Only</option>
                                <option value="hospital">Hospitals Only</option>
                            </select>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none bg-slate-50/50 font-bold text-slate-600"
                            >
                                <option value="all">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="contacted">Contacted</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* List Body */}
                <div className="flex-1 overflow-y-auto custom-scrollbar divide-y divide-slate-50">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-48 gap-3 text-slate-400">
                            <div className="w-8 h-8 border-4 border-slate-100 border-t-[#143E78] rounded-full animate-spin"></div>
                            <span className="text-xs font-bold uppercase tracking-wider">Loading Requests...</span>
                        </div>
                    ) : filteredRequests.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-slate-400 p-6 text-center">
                            <Inbox className="w-12 h-12 text-slate-200 mb-2" />
                            <p className="text-sm font-bold">No requests found matching filters.</p>
                        </div>
                    ) : (
                        filteredRequests.map(req => {
                            const isSelected = selectedRequest?._id === req._id;
                            const isDoc = req.type === 'doctor';
                            const title = isDoc ? req.fullName : req.hospitalName;
                            
                            return (
                                <div
                                    key={req._id}
                                    onClick={() => setSelectedRequest(req)}
                                    className={`p-5 cursor-pointer transition-all flex gap-4 items-start ${
                                        isSelected ? 'bg-blue-50/50 border-l-4 border-l-[#143E78]' : 'hover:bg-slate-50/50'
                                    }`}
                                >
                                    <div className={`p-2.5 rounded-xl text-white ${isDoc ? 'bg-sky-500' : 'bg-emerald-500'} shadow-sm`}>
                                        {isDoc ? <Stethoscope className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
                                    </div>
                                    <div className="min-w-0 flex-1 space-y-1">
                                        <div className="flex justify-between items-start gap-2">
                                            <h4 className="text-sm font-black text-slate-800 leading-tight truncate">{title}</h4>
                                            {getStatusBadge(req.status)}
                                        </div>
                                        {isDoc && req.specialization && (
                                            <p className="text-[11px] font-bold text-[#143E78] truncate">{req.specialization}</p>
                                        )}
                                        {!isDoc && req.contactPerson && (
                                            <p className="text-[11px] font-bold text-slate-500 truncate">Contact: {req.contactPerson}</p>
                                        )}
                                        <div className="flex items-center gap-4 text-[10px] text-slate-400 font-semibold pt-1">
                                            <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {req.phone}</span>
                                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(req.createdAt).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Right Column: Request details */}
            <div className="hidden md:flex flex-col flex-1 h-full bg-slate-50/30 overflow-hidden">
                {selectedRequest ? (
                    <div className="flex flex-col h-full overflow-hidden">
                        {/* Header Details */}
                        <div className="bg-white p-6 border-b border-slate-100 flex flex-wrap gap-4 items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-2xl text-white ${selectedRequest.type === 'doctor' ? 'bg-sky-500' : 'bg-emerald-500'} shadow-md`}>
                                    {selectedRequest.type === 'doctor' ? <Stethoscope className="w-6 h-6" /> : <Building2 className="w-6 h-6" />}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider ${
                                            selectedRequest.type === 'doctor' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'
                                        }`}>
                                            {selectedRequest.type}
                                        </span>
                                        {getStatusBadge(selectedRequest.status)}
                                    </div>
                                    <h2 className="text-lg font-black text-slate-800 leading-tight">
                                        {selectedRequest.type === 'doctor' ? selectedRequest.fullName : selectedRequest.hospitalName}
                                    </h2>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handleInvitePartner(selectedRequest)}
                                    className="px-3 py-2 bg-[#143E78] hover:bg-blue-800 text-white font-bold text-xs rounded-xl shadow-md shadow-blue-500/10 transition-all flex items-center gap-1.5"
                                >
                                    <Send className="w-4 h-4" /> Invite Partner
                                </button>
                                {selectedRequest.status !== 'contacted' && (
                                    <button
                                        onClick={() => handleUpdateStatus(selectedRequest._id, 'contacted')}
                                        className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow-md shadow-amber-500/10 transition-all flex items-center gap-1.5"
                                    >
                                        <Clock className="w-4 h-4" /> Mark Contacted
                                    </button>
                                )}
                                {selectedRequest.status !== 'approved' && (
                                    <button
                                        onClick={() => handleUpdateStatus(selectedRequest._id, 'approved')}
                                        className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs rounded-xl shadow-md shadow-emerald-500/10 transition-all flex items-center gap-1.5"
                                    >
                                        <CheckCircle2 className="w-4 h-4" /> Approve
                                    </button>
                                )}
                                {selectedRequest.status !== 'rejected' && (
                                    <button
                                        onClick={() => handleUpdateStatus(selectedRequest._id, 'rejected')}
                                        className="px-3 py-2 bg-rose-500 hover:bg-rose-600 text-white font-bold text-xs rounded-xl shadow-md shadow-rose-500/10 transition-all flex items-center gap-1.5"
                                    >
                                        <XCircle className="w-4 h-4" /> Reject
                                    </button>
                                )}
                                {user?.role === 'admin' && (
                                    <button
                                        onClick={() => handleDelete(selectedRequest._id)}
                                        className="p-2 bg-rose-50 hover:bg-rose-100 text-rose-500 hover:text-rose-600 rounded-xl transition-all"
                                        title="Delete Permanently"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Details Grid */}
                        <div className="flex-1 p-6 space-y-6 overflow-y-auto custom-scrollbar">
                            {/* Core details card */}
                            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm space-y-6">
                                <h3 className="text-xs font-black uppercase text-[#143E78] tracking-[0.2em] flex items-center gap-2">
                                    <User className="w-4 h-4 text-blue-500" /> Lead Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Email Address</span>
                                        <a href={`mailto:${selectedRequest.email}`} className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1.5">
                                            <Mail className="w-3.5 h-3.5" /> {selectedRequest.email}
                                        </a>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Phone Number</span>
                                        <a href={`tel:${selectedRequest.phone}`} className="text-xs font-bold text-[#143E78] hover:underline flex items-center gap-1.5">
                                            <Phone className="w-3.5 h-3.5" /> {selectedRequest.phone}
                                        </a>
                                    </div>
                                    {selectedRequest.city && (
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">City</span>
                                            <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                                                <MapPin className="w-3.5 h-3.5 text-rose-500" /> {selectedRequest.city}
                                            </span>
                                        </div>
                                    )}
                                    {selectedRequest.type === 'hospital' && selectedRequest.contactPerson && (
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Contact Person</span>
                                            <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                                                <User className="w-3.5 h-3.5 text-emerald-500" /> {selectedRequest.contactPerson}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {selectedRequest.address && (
                                    <div className="pt-4 border-t border-slate-50 space-y-1">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Street Address</span>
                                        <span className="text-xs font-bold text-slate-700">{selectedRequest.address}</span>
                                    </div>
                                )}
                            </div>

                            {/* Doctor Professional Details */}
                            {selectedRequest.type === 'doctor' && (
                                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm space-y-6">
                                    <h3 className="text-xs font-black uppercase text-[#143E78] tracking-[0.2em] flex items-center gap-2">
                                        <Award className="w-4 h-4 text-amber-500" /> Professional Credentials
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-slate-400 uppercase block">Undergraduate Degree</span>
                                            <span className="text-xs font-black text-slate-800">{selectedRequest.degree === 'Others' ? selectedRequest.otherDegree : selectedRequest.degree || '—'}</span>
                                        </div>
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-slate-400 uppercase block">Post-Grad / Specialization</span>
                                            <span className="text-xs font-black text-slate-800">{selectedRequest.specialization === 'Others' ? selectedRequest.otherSpecialization : selectedRequest.specialization || 'None'}</span>
                                        </div>
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-slate-400 uppercase block">Super-Specialization</span>
                                            <span className="text-xs font-black text-slate-800">{selectedRequest.superSpecialization === 'Others' ? selectedRequest.otherSuperSpecialization : selectedRequest.superSpecialization || 'None'}</span>
                                        </div>
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-slate-400 uppercase block">Years of Experience</span>
                                            <span className="text-xs font-black text-[#143E78] bg-blue-50 px-3 py-1 rounded-lg inline-block mt-0.5">{selectedRequest.experience || '—'} Years</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Internal notes */}
                            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm space-y-6">
                                <h3 className="text-xs font-black uppercase text-[#143E78] tracking-[0.2em] flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-[#143E78]" /> Internal Admin Notes
                                </h3>

                                {/* Notes List */}
                                <div className="space-y-4 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                                    {selectedRequest.notes?.length === 0 ? (
                                        <p className="text-xs text-slate-400 font-bold italic">No internal notes logged yet.</p>
                                    ) : (
                                        selectedRequest.notes.map((note, idx) => (
                                            <div key={idx} className="bg-slate-50 p-4 rounded-2xl space-y-1 border border-slate-100">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[10px] font-black text-[#143E78]">
                                                        {note.addedBy?.firstName} {note.addedBy?.lastName || 'Operations Agent'}
                                                    </span>
                                                    <span className="text-[9px] font-bold text-slate-400">
                                                        {new Date(note.addedAt).toLocaleString()}
                                                    </span>
                                                </div>
                                                <p className="text-xs font-semibold text-slate-700 leading-relaxed">{note.content}</p>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* Add Note Form */}
                                <form onSubmit={handleAddNote} className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Add an internal note..."
                                        value={newNote}
                                        onChange={(e) => setNewNote(e.target.value)}
                                        className="flex-1 px-4 py-2.5 text-xs border border-slate-200 rounded-xl outline-none focus:border-[#143E78] transition-all bg-slate-50/50 font-semibold"
                                    />
                                    <button
                                        type="submit"
                                        disabled={!newNote.trim() || actionLoading}
                                        className="px-4 bg-[#143E78] hover:bg-[#1f5091] text-white rounded-xl transition-all shadow-md shadow-blue-500/10 flex items-center justify-center disabled:opacity-40"
                                    >
                                        <Send className="w-4 h-4" />
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400 p-6 text-center">
                        <Inbox className="w-16 h-16 text-slate-200 mb-2" />
                        <h3 className="text-sm font-black uppercase tracking-wider">No Request Selected</h3>
                        <p className="text-xs font-semibold max-w-xs mt-1">Select an onboarding entry from the side catalog to view qualifications, documents, and manage state.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OnboardingRequests;
